interface OrderedBlock {
  type: string;
  order: number;
}

interface BlockListConfig<T> {
  version?: number;
  blocks?: T[];
}

// Blocks nearly every template type carries, so they say nothing about which
// template a saved block list was written for.
const GENERIC_BLOCK_TYPES = new Set(["mobile_app_promo", "signature"]);

function savedBlocksOf<T>(config: BlockListConfig<T> | null | undefined): T[] {
  return config?.version === 2 && Array.isArray(config.blocks) ? config.blocks : [];
}

/**
 * The saved block list, when it was written for this template. A list holding
 * none of the template's own sections was written for another template type,
 * or is the genie's shared brand template (type: null) that
 * getTemplateWithFallback returns for any type the genie never customized —
 * that yields null and the template's defaults apply. Same rule as the Email
 * Templates editor (useEmailTemplateConfig.updateConfig), so the preview is
 * what gets sent.
 */
export function ownSavedBlocks<T extends OrderedBlock>(
  config: BlockListConfig<T> | null | undefined,
  defaults: T[],
): T[] | null {
  const savedBlocks = savedBlocksOf(config);
  const ownTypes = new Set(defaults.map((b) => b.type).filter((type) => !GENERIC_BLOCK_TYPES.has(type)));
  return savedBlocks.some((b) => ownTypes.has(b.type)) ? savedBlocks : null;
}

/**
 * The template's default blocks, keeping whatever was saved for the generic
 * ones — a signature switched on from Branding lives only there.
 */
export function defaultBlocksWithSavedGeneric<T extends OrderedBlock>(
  config: BlockListConfig<T> | null | undefined,
  defaults: T[],
): T[] {
  const savedBlocks = savedBlocksOf(config);
  return defaults.map((block) => {
    const saved = savedBlocks.find((b) => b.type === block.type && GENERIC_BLOCK_TYPES.has(b.type));
    return saved ? { ...saved, order: block.order } : block;
  });
}
